import { StyledHTMLFieldsBlock } from 'components/StyledHTMLFiledsBlock/StyledHTMLFieldsBlock';
import s from './SupportSettings.module.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DropdownBlock } from 'components/DropdownBlock/DropdownBlock';
import { useDeleteQuestionMutation, useGetQuestionsQuery, useGetSettingsQuery, useSaveSettingsMutation } from '../../../../redux/apis/supportApi';
import { ReactComponent as Arrow } from "../../../../assets/icons/arrow_tools.svg";
import { ReactComponent as EditIcon } from "../../../../assets/icons/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../../assets/icons/delete_icon.svg";
import { format } from 'date-fns';
import { useGetLocale } from 'utils/hooks/useGetLocale';
import { PrimaryButton } from 'components/PrimaryButton/PrimaryButton';
import { useNavigate } from 'react-router-dom';
import {ReactComponent as ReadIcon} from "../../../../assets/icons/read.svg";
import questionMark from "assets/images/questionMark.webp";
import { useStatusContext } from 'components/StatusProvider';
import { ModalGeneral } from 'components/Modals/ModalGeneral';
import { Skeleton } from 'components/Skeleton/Skeleton';
import { ValidateTextFields } from 'utils/validation';
import { addScaleCorrector } from 'framer-motion';


export const SupportSettings = () => {

    // ### State
    const [form, setForm] = useState<any>({email: undefined, isEmail: false})
    const [dropdown, setDropdown] = useState({default: true, emails: false})
    const [showDeleteModal, setShowDeleteModal] = useState<any>(false);
    const langs = useSelector((store: any) => store.main.langs);
    const [activeLang, setActiveLang] = useState<string>(langs?.[0]?.key)
    const {t} = useTranslation()
    const locale = useGetLocale()
    const navigate = useNavigate()
    const status = useStatusContext()

    const {data: questions, isLoading: isGetQuestionsLoading} = useGetQuestionsQuery({})
    const [DeleteQuestion, {isLoading: isDeleteLoading}] = useDeleteQuestionMutation()
    const [SaveSettings, {isLoading: isSaveSettingsLoading}] = useSaveSettingsMutation()
    const {data: settings, isLoading: isGetSettingsLoading} = useGetSettingsQuery({})
    
    
    // ### Effects 
    useEffect(()=>{
        if(settings?.id){
            setForm(settings)
        }
    },[settings]);
    
    // ### Functions
    async function handleDelete(id: any){
        const response = await DeleteQuestion({id})
        if(response.data?.message === "deleted"){
            status.openStatus("success", "") 
            navigate('/support/settings')
        }
        else status.openStatus('error', "Error")
    }

    async function handleSaveSettings(){
        let error
        if(!ValidateTextFields(form.email, langs)) error = "support.errors.email"

        if(error && form.isEmail){
            status.openStatus('error', error)
            return
        }

        const response = await SaveSettings({isEmail: form.isEmail, email: form.email})
        if(response.data?.message === "ok"){
            status.openStatus("success", "") 
        }
        else status.openStatus('error', "Error")
    }

    // ### Views
    const questionsView = questions?.map((question: any, index: number) => {
        return(
            <div key={index} className={s.question}>
                <div className={s.message_wrapper}>
                    <p className={s.author}>A</p>
                    <div className={`${s.message} ${s.my_messagee}`}>
                        <p dangerouslySetInnerHTML={{__html: question.question[activeLang]}}></p>
                        <div className={s.absolute_wrapper}>
                            <span className={s.date}>{format(new Date(), "d MMMM yyyy HH:mm", { locale: locale })}</span>
                        </div>
                    </div>
                </div>
                <div className={s.message_wrapper}>
                    <p className={s.author}>B</p>
                    <div className={`${s.message} ${s.my_message}`}>
                        <p dangerouslySetInnerHTML={{__html: question.answer[activeLang]}}></p>
                        <div className={s.absolute_wrapper}>
                            <span className={s.date}>{format(new Date(), "d MMMM yyyy HH:mm", { locale: locale })}</span>
                            <ReadIcon/>
                        </div>
                    </div>
                </div>
                <div className={s.edit_wrapper} onClick={()=>navigate(`/support/settings/create/${question.id}`)}>
                    <EditIcon/>
                </div>
                <div className={s.delete_wrapper} onClick={()=>setShowDeleteModal(question.id)}>
                    <DeleteIcon/>
                </div>
            </div>
        )
    })

    return(
        <>
        <div className={s.dropdown_block}>
            <div className={`${s.header} ${dropdown?.default ? s.active : ""}`} onClick={() => setDropdown(prev => ({...prev, default: !prev.default}))}>
                <div className={`${s.arrow}`}><Arrow/></div>
                <span className={s.title}>{t('support.questions')}</span>
            </div>
            <DropdownBlock open={dropdown.default}>
                <div className={s.questions_wrapper}>
                    {questionsView}
                    <PrimaryButton
                        text='Add'
                        type={"button"}
                        style={{width: "fit-content"}}
                        onClick={()=>navigate('/support/settings/create')}
                        disabled={false}
                    />
                </div>
            </DropdownBlock>
        </div>

        <div className={s.dropdown_block}>
            <div className={`${s.header} ${dropdown?.emails ? s.active : ""}`} onClick={() => setDropdown(prev => ({...prev, emails: !prev.emails}))}>
                <div className={`${s.arrow}`}><Arrow/></div>
                <span className={s.title}>{t('support.emails_title')}</span>
            </div>
            <DropdownBlock open={dropdown.emails}>
                <StyledHTMLFieldsBlock
                    alternativeText={t('support.emails_description')}
                    setActive={(value: boolean) => setForm((prev: any) => ({...prev, isEmail: value}))}
                    active={form.isEmail}
                    values={form.email}
                    title={"Email"}
                    changeValue={(value)=> {setForm((prev: any) => ({...prev, email: {...prev.email, ...value}}))}}
                    activeLang={activeLang}
                    setActiveLang={setActiveLang}
                />
                <PrimaryButton 
                    text='Save'
                    type={"button"}
                    style={{width: "fit-content"}}
                    onClick={handleSaveSettings}
                    disabled={isSaveSettingsLoading}
                />
            </DropdownBlock>
        </div>

        <ModalGeneral
            showModal={showDeleteModal}
            setShowModal={setShowDeleteModal}
            title={t("general.delete")}
            textLeftButton={t("general.cancel")}
            textRightButton={t("general.delete")}
            image={questionMark}
            description={`${t("general.areYouSure")}? ${t("general.itsContents")}`}
            buttonRight={() => handleDelete(showDeleteModal)}
        />

        <Skeleton isLoading={(isGetQuestionsLoading || isDeleteLoading || isGetSettingsLoading)}/>
        </>
    )
}